import React from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import {
  ShowChart
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

/**
 * BacktestResultsCard Component
 * 
 * Displays backtest results with:
 * - Performance metrics (return, Sharpe, drawdown, win rate)
 * - Equity curve chart
 * - Trade history table
 * - Transaction cost summary
 * 
 * Phase 1 Days 12-14: Backtest Frontend UI
 * 
 * @param {Object} backtestResults - Backtest results from API
 * @param {boolean} loading - Loading state
 */
const BacktestResultsCard = ({ backtestResults, loading }) => {
  const { t } = useTranslation();

  if (!backtestResults) return null;

  const {
    success = false,
    total_return = 0,
    annualized_return = 0,
    volatility = 0,
    sharpe_ratio = 0,
    max_drawdown = 0,
    win_rate = 0,
    num_trades = 0,
    num_rebalances = 0,
    transaction_cost_pct = 0,
    portfolio_values = [],
    trades = []
  } = backtestResults;

  // Helper: Format percentage
  const formatPct = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  // Helper: Get return color
  const getReturnColor = (value) => {
    if (value > 0) return 'success.main';
    if (value < 0) return 'error.main';
    return 'text.primary';
  };

  // Helper: Get Sharpe color
  const getSharpeColor = (sharpe) => {
    if (sharpe >= 1.5) return 'success';
    if (sharpe >= 1.0) return 'warning';
    return 'error';
  };

  // Helper: Get Sharpe level
  const getSharpeLevel = (sharpe) => {
    if (sharpe >= 1.5) return t('backtest.levels.sharpe.excellent');
    if (sharpe >= 1.0) return t('backtest.levels.sharpe.good');
    if (sharpe >= 0.5) return t('backtest.levels.sharpe.fair');
    return t('backtest.levels.sharpe.poor');
  };

  // Helper: Get drawdown color
  const getDrawdownColor = (dd) => {
    const absDD = Math.abs(dd);
    if (absDD < 0.1) return 'success';
    if (absDD < 0.2) return 'warning';
    return 'error';
  };

  // Helper: Get drawdown level
  const getDrawdownLevel = (dd) => {
    const absDD = Math.abs(dd);
    if (absDD < 0.1) return t('backtest.levels.drawdown.low');
    if (absDD < 0.2) return t('backtest.levels.drawdown.moderate');
    return t('backtest.levels.drawdown.high');
  };

  // Prepare chart data
  const chartData = portfolio_values.map((item) => ({
    date: new Date(item.date).toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' }),
    value: item.value / 1_000_000_000, // Convert to billions
    fullDate: item.date
  }));

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{ 
          bgcolor: 'background.paper', 
          p: 1.5, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 2
        }}>
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mb: 0.5 }}>
            {new Date(data.fullDate).toLocaleDateString('vi-VN')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            {data.value.toFixed(2)}B VND
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (!success) {
    return (
      <Card sx={{ p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Typography variant="body2" color="error">
          {t('backtest.error')}: {backtestResults.error || 'Unknown error'}
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <ShowChart sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('backtest.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {num_rebalances} {t('backtest.rebalances')} • {num_trades} {t('backtest.trades')}
          </Typography>
        </Box>
      </Box>

      {/* Performance Metrics - Compact Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
        {/* Total Return */}
        <Tooltip title={t('backtest.tooltips.totalReturn')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.totalReturn')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: getReturnColor(total_return) }}>
              {formatPct(total_return)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Annualized Return */}
        <Tooltip title={t('backtest.tooltips.annualizedReturn')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.annualizedReturn')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: getReturnColor(annualized_return) }}>
              {formatPct(annualized_return)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Sharpe Ratio */}
        <Tooltip title={t('backtest.tooltips.sharpeRatio')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.sharpeRatio')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {sharpe_ratio.toFixed(2)}
            </Typography>
            <Chip 
              label={getSharpeLevel(sharpe_ratio)}
              color={getSharpeColor(sharpe_ratio)}
              size="small"
              sx={{ mt: 0.5, fontSize: '0.6rem', height: 18 }}
            />
          </Box>
        </Tooltip>

        {/* Max Drawdown */}
        <Tooltip title={t('backtest.tooltips.maxDrawdown')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.maxDrawdown')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'error.main' }}>
              {formatPct(max_drawdown)}
            </Typography>
            <Chip 
              label={getDrawdownLevel(max_drawdown)}
              color={getDrawdownColor(max_drawdown)}
              size="small"
              sx={{ mt: 0.5, fontSize: '0.6rem', height: 18 }}
            />
          </Box>
        </Tooltip>

        {/* Volatility */}
        <Tooltip title={t('backtest.tooltips.volatility')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.volatility')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatPct(volatility)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Win Rate */}
        <Tooltip title={t('backtest.tooltips.winRate')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.winRate')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatPct(win_rate)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Transaction Cost */}
        <Tooltip title={t('backtest.tooltips.transactionCost')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.transactionCost')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'warning.main' }}>
              {formatPct(transaction_cost_pct)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Number of Trades */}
        <Tooltip title={t('backtest.tooltips.numTrades')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('backtest.metrics.numTrades')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {num_trades}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Equity Curve Chart */}
      {chartData.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('backtest.equityCurve.title')}
          </Typography>
          <Box sx={{ width: '100%', height: 300, mb: 2 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  stroke="#666"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke="#666"
                  label={{ value: 'Tỷ VND', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '12px' }}
                  formatter={() => t('backtest.equityCurve.portfolioValue')}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#1976d2" 
                  strokeWidth={2}
                  dot={false}
                  name={t('backtest.equityCurve.portfolioValue')}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}

      {/* Trade History */}
      {trades && trades.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('backtest.tradeHistory.title')} ({trades.length} {t('backtest.tradeHistory.trades')})
          </Typography>
          <TableContainer sx={{ maxHeight: 250 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.date')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.ticker')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.action')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.shares')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.price')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.value')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('backtest.tradeHistory.cost')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.slice(0, 50).map((trade, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {new Date(trade.date).toLocaleDateString('vi-VN')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>
                      {trade.ticker}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Chip
                        label={trade.action}
                        color={trade.action === 'BUY' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {trade.shares?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {trade.price?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {((trade.value || 0) / 1_000_000).toFixed(2)}M
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75, color: 'warning.main' }}>
                      {((trade.cost || 0) / 1_000).toFixed(1)}K
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {trades.length > 50 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
              {t('backtest.tradeHistory.showing')} 50 / {trades.length} {t('backtest.tradeHistory.trades')}
            </Typography>
          )}
        </>
      )}
    </Card>
  );
};

export default BacktestResultsCard;
